import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
    PiArrowDownDuotone,
    PiArrowLeftDuotone,
    PiArrowRightDuotone,
    PiBriefcaseDuotone,
    PiGraduationCapDuotone,
    PiQuotesDuotone,
    PiTranslateDuotone,
    PiTrendUpDuotone,
} from 'react-icons/pi';

import Noise from 'elements/Noise';
import Pattern from 'elements/Pattern';

const benefits = [
    {
        icon: PiGraduationCapDuotone,
        title: 'Gyakorlati tapasztalat',
        text: 'Nagy figyelmet fordítunk arra, hogy gyakornoki időd alatt tényleges szakmai tudást szerezz. Az általunk közvetített gyakornoki programok sokszínűek, szakmai tudást, kapcsolatépítést és olyan lehetőségeket adnak, amik a munkaerőpiacra kilépve előnyt jelentenek.',
    },
    {
        icon: PiBriefcaseDuotone,
        title: 'Szakmai területek',
        text: 'Jelenlegi partnercégeink az IT/ programozás, különböző mérnöki (vegyipari mérnök, biomérnök, gépészmérnök, villamosmérnök), HR, marketing, kommunikáció, gazdasági, pénzügyi, műszaki területeken tevékenykednek. Ezek mellett, folyamatosan bővítjük a gyakornoki pozícióink palettáját. Célunk, hogy minél többféle lehetőség közül tudd kiválasztani a számodra megfelelő állást és megtapasztald, hogy milyen a tanult szakmádban dolgozni.',
    },
    {
        icon: PiTranslateDuotone,
        title: 'Idegennyelv-tudás',
        text: 'Ha már magabiztos idegennyelv-tudásod van és szívesen használnád a mindennapokban vagy fejlesztenéd még, akkor nálunk nyelvtudásodhoz passzoló állásokat is találsz. Közép-és nagyvállalati partnerinknél többnyire az angol nyelv legalább B2-es szintű ismerete szükséges. Ha emellett más idegennyelveket is beszélsz, csak előnyödre válik.',
    },
    {
        icon: PiTrendUpDuotone,
        title: 'Hosszútávú lehetőség',
        text: 'Ha nem csak a kötelező szakmai gyakorlat teljesítése a célod, hanem hosszútávra tervezel, akkor fontos kiemelnünk, hogy több partnerünknél van lehetőség a pozíciók közötti váltásra, illetve tapasztalataink alapján partnercégeink gyakran biztosítanak főállású munkaviszonyt, a tanulmányaid befejezése után.',
    },
];

const testimonials = [
    {
        text: 'Marketing gyakornokként dolgozom, elősorban a különböző marketing anyagok elkészítésével és legyártatásával foglalkozom. Számomra ez azért nagyon kedvelt faladat mert nagyon szeretek kreatív lenni, és használni a fantáziámat. Viszonylag kicsi csapatban dolgozom, de mindig nagyon jó a hangulat, mindig együtt eszünk, nagyon jól tudunk kommunikálni, nagyon jó a csoportdinamika, és ezért nagyon hálás lehetek. Teljes mértékben órarend barát a beosztás, és a feletteseim tisztelik, és tudják, hogy számomra az egyetemi tanulmányaim a legfontosabbak, véleményem szerint ez egy nagyon kivételes dolog.',
        author: 'K. Orsolya',
    },
    {
        text: 'Helyileg egy kicsit messze van a gyakornoki helyem, home officera egyelőre nincsen még lehetőség, de ennek ellenére a csapatmorál tökéletes, nagyon segítőkész minden kolléga. A fizetés abszolút korrekt. IT gyakornoki pozícióban dolgozom jelenleg, leginkább azt szeretem a munkámban, hogy számítógépen keresztül elérhetek más gépeket távolról, illetve admin jogokkal rendelkezem, és nagyobb projekteben vehetek részt, és segíthetem a vállalt fejlődédét. A munkahelyi étterem kiváló, illetve még kiemelkedően jónak tartom a sportolási lehetőséget cégen belül. A főnökök nagyon támogatóak gyakornokaikkal, és mindig szakmai segítséget is nyújtanak.',
        author: 'B. M. Zsolt',
    },
    {
        text: 'Jól érzem magam a csapatban. Elősorban az a feladatom, hogy segítsem a főállású dolgozókat, ami engem nagyon jó érzéssel tölt el, hogy meg tudom könnyíteni a mindennapi munkájukat. Rugalmas, órarendbarát a beosztás. Lehet home officeban is dolgozni, ez az, ami miatt tuti nem váltanék gyakornoki helyet.',
        author: 'Sz. Károly',
    },
    {
        text: 'Nagyon szeretem a munkahelyi csapatot, nagyon segítőkészek, illetve nem csak kávét főzök, hanem valódi szakmai tudásra tehetek szert nap mint nap. Nagyon jók a vezetők, szakmai segítséget nyújtanak mindenben. Rendezett az irodai környezet, hétfőtől péntekig tart az irodai munkavégzés, rugalmas beosztással és szabad hétvégékkel, de egyébként home officra is van lehetőség.',
        author: 'Z. Tünde Édua',
    },
    {
        text: 'Egyetemi szakmai gyakorlatomat nagyon szeretem, nagyon jó a csapat, sokat tanulok szakmailag, és úgy tűnik ők is nagyon meg vannak velem elégedve, mivel eredetileg úgy volt, hogy év végig dolgozom majd náluk, és ezt most a vezetőség meghosszabbította egy fél évvel. A cég megközelíthetősége szuper, igazi multis a környezet. Az iroda gyönyörű, nagyon odafigyelnek környezettudatosságra. Szeptember elejéig nem kellett irodán dolgoznunk, és most a vírus miatt teljesen önkéntes a bejárás. A csapat fiatalos, lendületes, és csajos, illetve nagyon összetartó. Ami a legpozitívabb, hogy mindenki rengeteget dolgozik, mégis mindig elérhetőek a kollégák, és a segítségemre vannak.',
        author: 'H. Sára Rebeka',
    },
];

const internshipAreas = [
    {
        title: 'Pénzügy',
        text: 'számvitel, pénzügyi jog, adójog, adótanácsadás',
        image: '/assets/images/penzugy_asztali.jpg',
        icon: '/assets/images/icons/penzugy_narancs.svg',
    },
    {
        title: 'Informatika',
        text: 'rendszerinformatika, szoftver- és applikációfejlesztés',
        image: '/assets/images/informatika_asztali.jpg',
        icon: '/assets/images/icons/informatika_narancs.svg',
    },
    {
        title: 'Személyügy',
        text: 'HR, munkaügy',
        image: '/assets/images/szemelyugy_asztali.jpg',
        icon: '/assets/images/icons/szemelyugy_narancs.svg',
    },
    {
        title: 'Marketing',
        text: 'marketing, kommunikáció, PR',
        image: '/assets/images/marketing_asztali.jpg',
        icon: '/assets/images/icons/marketing_narancs.svg',
    },
    {
        title: 'Logisztika',
        text: 'logisztika, szállítmányozás',
        image: '/assets/images/logisztika_asztali.jpg',
        icon: '/assets/images/icons/logisztika_narancs.svg',
    },
    {
        title: 'Mérnök',
        text: 'gépészmérnök, üzem mérnök, informatikai mérnök',
        image: '/assets/images/mernok_asztali.jpg',
        icon: '/assets/images/icons/mernok_narancs.svg',
    },
];

export default function GyakornokMainPage() {
    const scrollToContent = () => {
        document.getElementById('gyakornok-elonyok')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <main className='w-full bg-white/20'>
            <section className='relative z-20 min-h-[calc(100svh-5rem)] overflow-hidden rounded-b-3xl shadow-2xl isolate'>
                <img
                    src='/assets/images/background/asztalibanner1.jpg'
                    alt='Egyetemisták közösen tanulnak és dolgoznak'
                    className='absolute inset-0 size-full object-cover object-center'
                />
                <div className='absolute inset-0 bg-gradient-to-t from-orange-950 via-orange-950/65 to-orange-950/20 md:bg-gradient-to-r md:from-orange-950/95 md:via-orange-950/50 md:to-orange-950/15' />
                <Noise />

                <div className='relative z-10 mx-auto flex min-h-[calc(100svh-5rem)] w-full max-w-boxed items-center px-site py-20'>
                    <div className='flex max-w-5xl flex-col items-center text-center md:items-start md:text-left'>
                        <h1 className='font-display text-4xl font-black leading-[0.98] text-white text-balance sm:text-6xl lg:text-7xl xl:text-8xl'>
                            Gyakornoki állásaink <span className='text-orange-400'>megalapozzák a jövőd és a karriered</span>
                        </h1>
                        <p className='mt-6 max-w-3xl text-base leading-relaxed text-white/85 sm:text-lg lg:text-xl'>
                            Szeretnéd már az egyetemi éveid alatt kipróbálni a gyakorlatban tanulmányaidat?<br />
                            Érdekel, hogyan néz ki a szakmai területed a mindennapokban?<br />
                            Találd meg a hozzád közelebb álló gyakornoki helyet!
                        </p>
                        <div className='mt-8 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row'>
                            <Link to='/diakmunka' className='button gradient-yellow shadow-xl shadow-orange-950/20'>
                                Megnézem az állásokat <PiArrowRightDuotone />
                            </Link>
                            <button type='button' onClick={scrollToContent} className='button border border-white/20 bg-orange-950/35 backdrop-blur-md hover:bg-orange-950/50'>
                                Olvass tovább <PiArrowDownDuotone />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section id='gyakornok-elonyok' className='relative z-10 -mt-6 scroll-mt-20 overflow-hidden rounded-b-3xl bg-orange-50 px-site pb-20 pt-24 shadow-2xl sm:pb-24 sm:pt-28'>
                <div className='mx-auto flex w-full max-w-boxed flex-col gap-10 sm:gap-12'>
                    <SectionHeading title='Miért jó, Fürge Gyakornoknak lenni?' />

                    <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
                        {benefits.map((item) => <BenefitCard key={item.title} item={item} />)}
                    </div>
                </div>
            </section>

            <TestimonialSection />

            <section className='relative z-0 -mt-6 overflow-hidden rounded-b-3xl bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 px-site pb-20 pt-28 shadow-2xl sm:pb-24 sm:pt-32'>
                <Pattern className='opacity-20' />
                <div className='relative z-10 mx-auto flex w-full max-w-boxed flex-col gap-10 sm:gap-12'>
                    <SectionHeading title='Gyakornoki területeink' />

                    <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                        {internshipAreas.map((item) => <AreaCard key={item.title} item={item} />)}
                    </div>

                    <div className='flex justify-center'>
                        <Link to='/diakmunka' className='button gradient-yellow w-full shadow-xl sm:w-auto'>
                            Megnézem az állásokat <PiArrowRightDuotone />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}

function TestimonialSection() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const reducedMotion = useReducedMotion();

    useEffect(() => {
        const interval = window.setInterval(() => {
            setCurrentIndex((index) => (index + 1) % testimonials.length);
        }, 8000);

        return () => window.clearInterval(interval);
    }, []);

    const changeTestimonial = (direction) => {
        setCurrentIndex((index) => (index + direction + testimonials.length) % testimonials.length);
    };

    const current = testimonials[currentIndex];

    return (
        <section className='relative z-20 overflow-hidden rounded-b-3xl bg-gradient-to-br from-orange-700 via-orange-600 to-amber-500 px-site py-16 shadow-2xl sm:py-24'>
            <img src='/assets/images/background/asztalibanner2.jpg' alt='' className='absolute inset-0 size-full object-cover object-center opacity-25 mix-blend-soft-light' />
            <Noise />

            <div className='relative z-10 mx-auto flex w-full max-w-boxed flex-col gap-10'>
                <SectionHeading title='Mit mondanak jelenlegi Fürge Gyakornokaink?' light />

                <div className='grid grid-cols-1 items-center gap-6 sm:grid-cols-[auto_1fr_auto]'>
                    <SliderButton className='hidden sm:flex' label='Előző vélemény' onClick={() => changeTestimonial(-1)}>
                        <PiArrowLeftDuotone />
                    </SliderButton>

                    <div className='min-w-0 overflow-hidden rounded-3xl border border-white/60 bg-white/90 p-6 shadow-2xl shadow-orange-950/20 backdrop-blur-md sm:p-10 lg:p-14' aria-live='polite'>
                        <AnimatePresence mode='wait' initial={false}>
                            <motion.figure
                                key={currentIndex}
                                initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -18 }}
                                transition={{ duration: 0.4, ease: 'easeOut' }}
                                className='m-0'
                            >
                                <PiQuotesDuotone className='mb-5 size-10 text-orange-500 sm:size-12' />
                                <blockquote className='max-h-[55svh] overflow-y-auto pr-2 text-sm leading-relaxed text-neutral-700 sm:max-h-none sm:text-base lg:text-lg'>{current.text}</blockquote>
                                <figcaption className='mt-6 font-display text-xl font-black text-orange-600 sm:text-2xl'>{current.author}</figcaption>
                            </motion.figure>
                        </AnimatePresence>
                    </div>

                    <SliderButton className='hidden sm:flex' label='Következő vélemény' onClick={() => changeTestimonial(1)}>
                        <PiArrowRightDuotone />
                    </SliderButton>
                </div>

                <div className='flex items-center justify-between gap-4 sm:justify-center'>
                    <SliderButton className='flex sm:hidden' label='Előző vélemény' onClick={() => changeTestimonial(-1)}>
                        <PiArrowLeftDuotone />
                    </SliderButton>
                    <div className='flex justify-center gap-2'>
                        {testimonials.map((item, index) => (
                            <button
                                key={item.author}
                                type='button'
                                onClick={() => setCurrentIndex(index)}
                                aria-label={`${index + 1}. vélemény`}
                                aria-current={currentIndex === index ? 'true' : undefined}
                                className={'h-2.5 rounded-full transition-all ' + (currentIndex === index ? 'w-10 bg-white' : 'w-2.5 bg-white/40 hover:bg-white/70')}
                            />
                        ))}
                    </div>
                    <SliderButton className='flex sm:hidden' label='Következő vélemény' onClick={() => changeTestimonial(1)}>
                        <PiArrowRightDuotone />
                    </SliderButton>
                </div>
            </div>
        </section>
    );
}

function SectionHeading({ title, light = false }) {
    return (
        <h2 className={'max-w-5xl font-display text-3xl font-black leading-tight text-balance sm:text-4xl lg:text-5xl xl:text-6xl ' + (light ? 'text-white' : 'text-orange-600')}>
            {title}
        </h2>
    );
}

function BenefitCard({ item }) {
    const Icon = item.icon;

    return (
        <article className='group rounded-3xl border border-orange-100 bg-white p-6 shadow-xl shadow-orange-900/5 transition duration-500 hover:-translate-y-1 hover:border-orange-200 hover:shadow-2xl sm:p-8'>
            <span className='flex size-14 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 transition group-hover:bg-orange-500 group-hover:text-white'>
                <Icon className='size-7' />
            </span>
            <h3 className='mt-8 font-display text-2xl font-black leading-tight text-orange-600 sm:text-3xl'>{item.title}</h3>
            <p className='mt-3 text-sm leading-relaxed text-neutral-600 sm:text-base'>{item.text}</p>
        </article>
    );
}

function SliderButton({ className = '', label, onClick, children }) {
    return (
        <button
            type='button'
            onClick={onClick}
            aria-label={label}
            className={'size-10 items-center justify-center rounded-2xl border border-white/30 bg-orange-800/45 text-xl text-white shadow-lg backdrop-blur-md transition hover:scale-105 hover:bg-orange-800/70 sm:size-14 sm:text-2xl ' + className}
        >
            {children}
        </button>
    );
}

function AreaCard({ item }) {
    return (
        <Link to='/diakmunka' className='group overflow-hidden rounded-3xl bg-white shadow-2xl shadow-orange-900/10 transition duration-500 hover:-translate-y-2'>
            <div className='relative aspect-[16/10] overflow-hidden'>
                <img src={item.image} alt='' className='size-full object-cover transition duration-700 group-hover:scale-105' />
                <span className='absolute bottom-4 left-5 flex size-14 items-center justify-center rounded-2xl bg-white shadow-xl'>
                    <img src={item.icon} alt='' className='size-8' />
                </span>
            </div>
            <div className='p-6'>
                <h3 className='font-display text-2xl font-black text-orange-600 sm:text-3xl'>{item.title}</h3>
                <p className='mt-2 text-sm leading-relaxed text-neutral-600 sm:text-base'>{item.text}</p>
            </div>
        </Link>
    );
}
